import baemingoVideo from './videos/baemingo/bg.mp4';
import andysburgerVideo from './videos/andysburger/bg.mp4';
import zocaloDKVideo from './videos/zocaloDK/bg.mp4';

/**
 * Background videos bundled with the app for specific customers, copied from
 * the self-service kiosk themes (SelfServiceKiosks/Assets/video). Only videos
 * live here - no images.
 */
export const THEME_VIDEOS: Record<string, string> = {
  baemingo: baemingoVideo,
  andysburger: andysburgerVideo,
  zocaloDK: zocaloDKVideo,
};

/**
 * Company id -> key in THEME_VIDEOS, for the customers that have a video.
 * Copied from SelfServiceKiosks/config.ts (dev and production ids).
 */
const COMPANY_ID_TO_THEME: Record<string, string> = {
  /** Development */
  ckmeneb81avc90877is3cslwa: 'baemingo',
  cldaemmcwhxrn0800ighcmest: 'andysburger',
  // cm14xra4vcgqi0c27wsd57brk: 'andysburger',
  /** Production */
  cl1vtpvb0p9p90975p2bzluvb: 'baemingo',
  cld8h9zcglx3d0b872fdyr6c4: 'andysburger',
  clv6nuey94udl0b78yrf7rdj2: 'zocaloDK',
};

/** File extensions backoffice can upload as a background video. */
const VIDEO_EXTENSIONS = ['mp4', 'webm', 'mov', 'm4v', 'ogv', 'ogg'];

/**
 * backoffice's `backgroundMediaSrcUrl` is "an image or video" with no type
 * field, so tell them apart by the file extension in the URL path (query
 * string and hash ignored).
 */
export const isVideoUrl = (url: string): boolean => {
  const path = url.split(/[?#]/)[0].toLowerCase();
  const extension = path.slice(path.lastIndexOf('.') + 1);
  return VIDEO_EXTENSIONS.includes(extension);
};

/** The bundled video for a company, if it has one. */
export const getThemeVideo = (companyId: string | null | undefined): string | undefined => {
  if (!companyId) return undefined;
  const theme = COMPANY_ID_TO_THEME[companyId];
  return theme ? THEME_VIDEOS[theme] : undefined;
};

export type BackgroundMedia =
  | { type: 'video'; src: string; source: 'backoffice' | 'theme' }
  | { type: 'image'; src: string }
  | null;

/**
 * Picks the EPOS stage background, in priority order:
 * 1. backoffice media that is a video
 * 2. a bundled theme video for the company
 * 3. backoffice media (an image)
 * 4. nothing
 */
export const resolveBackgroundMedia = (
  backgroundMediaSrcUrl: string | null | undefined,
  companyId: string | null | undefined,
): BackgroundMedia => {
  if (backgroundMediaSrcUrl && isVideoUrl(backgroundMediaSrcUrl)) {
    return { type: 'video', src: backgroundMediaSrcUrl, source: 'backoffice' };
  }

  const themeVideo = getThemeVideo(companyId);
  if (themeVideo) {
    return { type: 'video', src: themeVideo, source: 'theme' };
  }

  if (backgroundMediaSrcUrl) {
    return { type: 'image', src: backgroundMediaSrcUrl };
  }

  return null;
};
